import { useEffect, useRef } from 'react'

import { NotificationInfoFragment } from '@sketch/gql-types'

import { useSetNotificationState } from './useSetNotificationState'

/**
 * Marks a notification as read the first time it's opened in the inbox preview,
 * if it's unread.
 *
 * This fires once per opened notification — we don't react to later `state`
 * changes, so the user is free to mark it back as unread (e.g. from the preview
 * menu) without it being immediately flipped to read again.
 */
export const useMarkNotificationAsReadOnOpen = (
  notification: NotificationInfoFragment | null | undefined
) => {
  const setNotificationState = useSetNotificationState()

  // Track every notification we've already acted on so the decision is made
  // exactly once per identifier — the first time it loads. Without this, the
  // cache write flipping `state` to UNREAD (the user marking it unread from the
  // menu) would re-run this effect and immediately mark it read again.
  const handledIdentifiers = useRef<Set<string>>(new Set())

  // Depend on the primitive fields, not the object, so the effect only re-runs
  // when they actually change. `typename` is needed because the cache entry is
  // keyed by the concrete notification type, not by identifier alone.
  const identifier = notification?.identifier
  const typename = notification?.__typename
  const state = notification?.state

  useEffect(() => {
    if (!identifier || !typename) return

    if (handledIdentifiers.current.has(identifier)) return
    handledIdentifiers.current.add(identifier)

    if (state !== 'UNREAD') return

    setNotificationState(identifier, typename, 'READ')
  }, [identifier, typename, state, setNotificationState])
}
